/**
 * Google Analytics 4 connector.
 *
 * Runs entirely in the buyer's browser. The access token is passed in by the
 * caller after being decrypted from IndexedDB. We never log or persist tokens
 * inside this module.
 */

import type {
  GA4Property,
  GA4ReportData,
  GA4DailyRow,
  GA4ReportTotals,
} from "@agencylens/shared";

const ADMIN_API_BASE = "https://analyticsadmin.googleapis.com/v1beta";
const DATA_API_BASE = "https://analyticsdata.googleapis.com/v1beta";

export class GA4Error extends Error {
  constructor(
    message: string,
    public readonly status?: number,
    public readonly googleCode?: string,
  ) {
    super(message);
    this.name = "GA4Error";
  }
}

/* --------------------------------------------------- listProperties */

interface AdminAccountSummary {
  name?: string; // "accountSummaries/12345"
  account?: string; // "accounts/12345"
  displayName?: string;
  propertySummaries?: AdminPropertySummary[];
}

interface AdminPropertySummary {
  property?: string; // "properties/123456789"
  displayName?: string;
  propertyType?: string;
}

interface AdminAccountSummariesResponse {
  accountSummaries?: AdminAccountSummary[];
  nextPageToken?: string;
}

export async function listProperties(
  accessToken: string,
): Promise<GA4Property[]> {
  const out: GA4Property[] = [];
  let pageToken: string | undefined;

  do {
    const url = new URL(`${ADMIN_API_BASE}/accountSummaries`);
    url.searchParams.set("pageSize", "200");
    if (pageToken) url.searchParams.set("pageToken", pageToken);

    const res = await fetch(url.toString(), {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      throw new GA4Error(
        `GA4 Admin API listAccountSummaries failed: ${res.status} ${res.statusText} ${body.slice(0, 200)}`,
        res.status,
      );
    }
    const data = (await res.json()) as AdminAccountSummariesResponse;

    for (const account of data.accountSummaries ?? []) {
      const accountResource = account.account ?? "";
      const accountDisplayName = account.displayName ?? "";
      for (const prop of account.propertySummaries ?? []) {
        if (!prop.property) continue;
        // GA4 properties have type PROPERTY_TYPE_ORDINARY; filter out subproperty/rollup types if needed.
        const propertyId = prop.property.split("/")[1] ?? "";
        out.push({
          property: prop.property,
          propertyId,
          displayName: prop.displayName ?? prop.property,
          account: accountResource,
          accountDisplayName,
        });
      }
    }

    pageToken = data.nextPageToken;
  } while (pageToken);

  return out;
}

/* ----------------------------------------------------- fetchReport */

interface RunReportResponse {
  rows?: Array<{
    dimensionValues?: Array<{ value?: string }>;
    metricValues?: Array<{ value?: string }>;
  }>;
  totals?: Array<{
    metricValues?: Array<{ value?: string }>;
  }>;
  rowCount?: number;
}

function parseNumber(s: string | undefined): number {
  if (!s) return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Convert a GA4 "YYYYMMDD" date string into "YYYY-MM-DD".
 */
function ga4DateToISO(d: string): string {
  if (d.length !== 8) return d;
  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
}

export async function fetchReport(
  accessToken: string,
  property: GA4Property,
  startDate: string,
  endDate: string,
): Promise<GA4ReportData> {
  const url = `${DATA_API_BASE}/${property.property}:runReport`;
  const body = {
    dateRanges: [{ startDate, endDate }],
    dimensions: [{ name: "date" }],
    metrics: [
      { name: "sessions" },
      { name: "totalUsers" },
      { name: "engagedSessions" },
      { name: "bounceRate" },
      { name: "conversions" },
    ],
    orderBys: [{ dimension: { dimensionName: "date" }, desc: false }],
    limit: "366",
  };

  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new GA4Error(
      `GA4 Data API runReport failed: ${res.status} ${res.statusText} ${text.slice(0, 300)}`,
      res.status,
    );
  }

  const json = (await res.json()) as RunReportResponse;
  const daily: GA4DailyRow[] = (json.rows ?? []).map((row) => {
    const date = ga4DateToISO(row.dimensionValues?.[0]?.value ?? "");
    const mv = row.metricValues ?? [];
    return {
      date,
      sessions: parseNumber(mv[0]?.value),
      totalUsers: parseNumber(mv[1]?.value),
      engagedSessions: parseNumber(mv[2]?.value),
      bounceRate: parseNumber(mv[3]?.value),
      conversions: parseNumber(mv[4]?.value),
    };
  });

  // GA4 Data API can return totals[] when requested; we compute locally to keep
  // bounceRate as a true average across rows rather than a sum.
  const totalsRow = json.totals?.[0]?.metricValues;
  const totals: GA4ReportTotals = {
    sessions: totalsRow
      ? parseNumber(totalsRow[0]?.value)
      : daily.reduce((s, r) => s + r.sessions, 0),
    totalUsers: totalsRow
      ? parseNumber(totalsRow[1]?.value)
      : daily.reduce((s, r) => s + r.totalUsers, 0),
    engagedSessions: totalsRow
      ? parseNumber(totalsRow[2]?.value)
      : daily.reduce((s, r) => s + r.engagedSessions, 0),
    bounceRate:
      daily.length > 0
        ? daily.reduce((s, r) => s + r.bounceRate, 0) / daily.length
        : 0,
    conversions: totalsRow
      ? parseNumber(totalsRow[4]?.value)
      : daily.reduce((s, r) => s + r.conversions, 0),
  };

  return {
    property,
    startDate,
    endDate,
    daily,
    totals,
    fetchedAt: new Date().toISOString(),
  };
}

/* ----------------------------------------------------- date helpers */

/** Returns "YYYY-MM-DD" in UTC. */
export function isoDate(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Returns an inclusive [start, end] covering the last `days` days, ending yesterday (GA4 today data is incomplete). */
export function defaultDateRange(days = 30): { startDate: string; endDate: string } {
  const now = new Date();
  const end = new Date(now);
  end.setUTCDate(end.getUTCDate() - 1);
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - (days - 1));
  return { startDate: isoDate(start), endDate: isoDate(end) };
}
